<template>
	<div
		v-click-outside.capture="closeMenu"
		class="relative inline-flex text-left"
		@keydown.esc.stop="closeOnEscape"
		@focusout="handleFocusOut"
	>
		<Button
			ref="trigger"
			type="tertiary"
			icon-only
			:disabled="loading"
			:aria-label="$t('application.more-actions')"
			:aria-expanded="isOpen ? 'true' : 'false'"
			aria-haspopup="menu"
			class="shrink-0 focus-visible:ring-2 focus-visible:ring-grey-950"
			data-test="overflow-trigger"
			@click.stop="toggleMenu"
		>
			<IconEllipsisVertical size="20" class="shrink-0" />
		</Button>

		<div
			v-if="isOpen"
			ref="menu"
			class="fixed z-[70] flex items-start gap-0"
			@mouseleave="hoverView('root')"
			@keydown.tab="closeMenu()"
		>
			<div
				v-if="view === 'stages'"
				ref="stages"
				class="flex w-max min-w-[253px] max-w-[calc(100vw-16px)] shrink-0 flex-col items-start rounded-lg bg-white p-4 shadow-lg ring-1 ring-inset ring-grey-200"
				role="menu"
				:aria-label="$t('application.move-stage.change-stage')"
				@keydown.down.prevent="focusWithin($refs.stages, 1)"
				@keydown.up.prevent="focusWithin($refs.stages, -1)"
				@keydown.left.prevent="setView('root', true)"
			>
				<button
					type="button"
					role="menuitem"
					tabindex="-1"
					:class="[itemClass, 'sm:hidden']"
					data-menu-back
					data-test="overflow-back"
					@click.stop="setView('root', true)"
				>
					<IconArrowLeft size="16" />{{
						$t('application.more-actions-back')
					}}
				</button>
				<MoveToStageOptions :status="status" @select="selectStage" />
			</div>

			<div
				v-if="view === 'downloads'"
				ref="downloads"
				:class="canChangeStage ? 'sm:mt-[45px]' : ''"
				class="flex w-max min-w-[267px] max-w-[calc(100vw-16px)] shrink-0 flex-col items-start gap-3 rounded-lg bg-white p-4 shadow-lg ring-1 ring-inset ring-grey-200"
				role="menu"
				:aria-label="$t('application.download.menu')"
				@keydown.down.prevent="focusWithin($refs.downloads, 1)"
				@keydown.up.prevent="focusWithin($refs.downloads, -1)"
				@keydown.left.prevent="setView('root', true)"
			>
				<button
					type="button"
					role="menuitem"
					tabindex="-1"
					:class="[itemClass, 'sm:hidden']"
					data-menu-back
					data-test="overflow-download-back"
					@click.stop="setView('root', true)"
				>
					<IconArrowLeft size="16" />{{
						$t('application.more-actions-back')
					}}
				</button>
				<button
					v-for="option in downloadOptions"
					:key="option.value"
					v-tooltip="option.reason"
					type="button"
					role="menuitem"
					tabindex="-1"
					class="w-full rounded-lg text-left text-sm text-grey-1000 hover:bg-grey-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-grey-950 aria-disabled:cursor-not-allowed aria-disabled:text-grey-500 aria-disabled:hover:bg-transparent"
					:aria-disabled="option.disabled ? 'true' : null"
					:aria-label="
						option.reason
							? `${$t(option.label)}: ${option.reason}`
							: null
					"
					:data-test="`overflow-download-${option.value}`"
					@click.stop="selectDownload(option)"
				>
					{{ $t(option.label) }}
				</button>
			</div>

			<div
				ref="mainMenu"
				:class="view === 'root' ? 'flex' : 'hidden sm:flex'"
				class="w-max min-w-[205px] shrink-0 flex-col items-start gap-1 rounded-lg bg-white p-2 shadow-lg ring-1 ring-inset ring-grey-200"
				role="menu"
				:aria-label="$t('application.more-actions')"
				@keydown.down.prevent="focusWithin($refs.mainMenu, 1)"
				@keydown.up.prevent="focusWithin($refs.mainMenu, -1)"
			>
				<button
					v-if="canChangeStage"
					ref="stagesTrigger"
					v-tooltip="stageLocked ? stageDisabledReason : null"
					type="button"
					tabindex="-1"
					role="menuitem"
					:class="[
						itemClass,
						'justify-between gap-12',
						view === 'stages' ? 'bg-grey-100' : ''
					]"
					:disabled="stageDisabled && !stageLocked"
					:aria-disabled="stageDisabled ? 'true' : null"
					:aria-expanded="view === 'stages' ? 'true' : 'false'"
					aria-haspopup="menu"
					data-test="overflow-change-stage"
					@mouseenter="hoverView('stages')"
					@click.stop="setView('stages', true)"
					@keydown.right.prevent="setView('stages', true)"
				>
					<span class="flex items-center gap-2">
						<IconArrowsSwap size="16" class="shrink-0" />
						{{ $t('application.move-stage.change-stage') }}
						<span v-if="stageLocked" class="sr-only">
							{{ stageDisabledReason }}
						</span>
					</span>
					<IconChevronDown size="12" class="shrink-0 -rotate-90" />
				</button>
				<button
					v-if="canDownload"
					ref="downloadsTrigger"
					type="button"
					tabindex="-1"
					role="menuitem"
					:class="[
						itemClass,
						'justify-between gap-12',
						view === 'downloads' ? 'bg-grey-100' : ''
					]"
					:aria-disabled="downloadLoading ? 'true' : null"
					:aria-busy="downloadLoading ? 'true' : null"
					:aria-expanded="view === 'downloads' ? 'true' : 'false'"
					aria-haspopup="menu"
					data-test="overflow-download-files"
					@mouseenter="hoverView('downloads')"
					@click.stop="setView('downloads', true)"
					@keydown.right.prevent="setView('downloads', true)"
				>
					<span class="flex items-center gap-2">
						<Spinner
							v-if="downloadLoading"
							size="sm"
							aria-hidden="true"
						/>
						<IconDownload v-else class="shrink-0" />
						{{ $t('application.download.menu') }}
					</span>
					<IconChevronDown size="12" class="shrink-0 -rotate-90" />
				</button>

				<button
					type="button"
					tabindex="-1"
					role="menuitem"
					:class="itemClass"
					data-test="overflow-copy-link"
					@mouseenter="hoverView('root')"
					@click.stop="copyLink"
				>
					<IconLink class="shrink-0" />
					{{ $t('application.copy-candidate-link') }}
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import Button from '~/components/ui/common/Button'
import Spinner from '~/components/ui/common/Spinner'
import {
	IconArrowLeft,
	IconDownload,
	IconArrowsSwap,
	IconChevronDown,
	IconEllipsisVertical,
	IconLink
} from '~/components/Icons'
import popoverMenu from '~/mixins/popoverMenu'
import { copyTextToClipboard } from '~/utils/clipboard'
import MoveToStageOptions from './MoveToStageOptions.vue'

export default {
	name: 'ApplicationOverflowMenu',
	components: {
		Button,
		Spinner,
		IconArrowLeft,
		IconDownload,
		IconArrowsSwap,
		IconChevronDown,
		IconEllipsisVertical,
		IconLink,
		MoveToStageOptions
	},
	mixins: [popoverMenu],
	props: {
		status: { type: String, default: null },
		canChangeStage: { type: Boolean, default: false },
		stageDisabled: { type: Boolean, default: false },
		stageDisabledReason: { type: String, default: null },
		loading: { type: Boolean, default: false },
		canDownload: { type: Boolean, default: false },
		hasCv: { type: Boolean, default: false },
		hasAttachments: { type: Boolean, default: false },
		attachmentsLoading: { type: Boolean, default: false },
		downloadLoading: { type: Boolean, default: false }
	},
	data() {
		return { view: 'root' }
	},
	computed: {
		downloadOptions() {
			return [
				{
					value: 'cv',
					label: 'application.download.profile-and-cv',
					available: this.hasCv,
					reasonKey: 'application.download.no-cv'
				},
				{
					value: 'all',
					label: 'application.download.profile-and-all',
					available: this.hasAttachments,
					reasonKey: 'application.download.no-attachments'
				}
			].map(option => ({
				...option,
				disabled:
					!option.available ||
					this.downloadLoading ||
					this.attachmentsLoading,
				reason: this.attachmentsLoading
					? this.$t('general.loading')
					: !option.available
					? this.$t(option.reasonKey)
					: null
			}))
		},
		// A reason keeps the item focusable so its tooltip reaches keyboard users.
		stageLocked() {
			return this.stageDisabled && Boolean(this.stageDisabledReason)
		},
		itemClass() {
			return 'flex w-full items-center gap-2 whitespace-nowrap rounded-lg p-2 text-left text-sm text-grey-1000 transition-colors hover:bg-grey-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-grey-950 disabled:cursor-not-allowed disabled:text-grey-500 disabled:hover:bg-transparent aria-disabled:cursor-not-allowed aria-disabled:text-grey-500 aria-disabled:hover:bg-transparent'
		}
	},
	watch: {
		isOpen(open) {
			if (!open) this.view = 'root'
		},
		downloadLoading(busy, wasBusy) {
			if (wasBusy && !busy) this.closeMenu({ restoreFocus: true })
		}
	},
	methods: {
		isDesktopMenu() {
			return window.matchMedia?.('(min-width: 640px)').matches ?? true
		},
		hoverView(view) {
			if (this.isDesktopMenu()) this.setView(view)
		},
		setView(view, focus = false) {
			if (view === 'stages' && this.stageDisabled) return
			if (
				view === 'downloads' &&
				(!this.canDownload || this.downloadLoading)
			)
				return
			const previous = this.view
			// Move focus before removing a view so Escape stays within the menu.
			if (focus) this.focusTrigger()
			else if (
				previous !== view &&
				this.$refs[previous]?.contains(document.activeElement)
			) {
				const trigger = this.$refs[`${previous}Trigger`]
				if (trigger) trigger.focus()
				else this.focusTrigger()
			}
			this.view = view
			if (previous === view && !focus) return
			this.$nextTick(() => {
				this.popper?.update()
				if (!focus) return
				if (view === 'root') {
					this.$refs[`${previous}Trigger`]?.focus()
				} else {
					this.$refs[view]
						?.querySelector(
							'[role="menuitem"]:not([data-menu-back]):not([disabled])'
						)
						?.focus()
				}
			})
		},
		selectDownload(option) {
			if (option.disabled) return
			this.setView('root', true)
			this.$emit('download', option.value)
		},
		focusWithin(root, direction) {
			const options = Array.from(
				root?.querySelectorAll('[role="menuitem"]:not([disabled])') ||
					[]
			).filter(
				option =>
					!option.hasAttribute('data-menu-back') ||
					!this.isDesktopMenu()
			)
			if (options.length === 0) return

			const currentIndex = options.indexOf(document.activeElement)
			const nextIndex =
				(currentIndex + direction + options.length) % options.length
			options[nextIndex].focus()
		},
		selectStage(status) {
			this.closeMenu()
			this.focusTrigger()
			this.$emit('select', status)
		},
		async copyLink() {
			this.closeMenu({ restoreFocus: true })
			if (!(await copyTextToClipboard(window.location.href))) return

			this.$store.dispatch('toast/push', {
				title: this.$t('toast.applicant-profile-link-copied.title'),
				message: this.$t('toast.applicant-profile-link-copied.message'),
				type: 'success'
			})
		}
	}
}
</script>
